import React, { useRef, useState } from "react";
import * as Select from "@radix-ui/react-select";
import styled from "styled-components/macro";
import { SIMPLE_STYLE_CONTROLS, SIMPLE_STYLE_OFF, type SimpleStyleControls } from "../../../../src/shared/simpleStyleControls";

type Control = (typeof SIMPLE_STYLE_CONTROLS)[number];

function ControlField({ control, value, onChange }: { control: Control; value?: string; onChange: (value: string) => void }) {
  const [open, setOpen] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  const options = [...control.options, SIMPLE_STYLE_OFF];
  const selected = options.find((option) => option.value === value) ?? SIMPLE_STYLE_OFF;
  const id = `simple-style-${control.id}`;
  return (
    <Field ref={fieldRef}>
      <label
        id={`${id}-label`}
        htmlFor={id}
      >
        {control.label}
      </label>
      <Select.Root
        value={selected.value}
        onValueChange={onChange}
        open={open}
        onOpenChange={setOpen}
      >
        <Trigger
          id={id}
          aria-labelledby={`${id}-label`}
        >
          <Select.Value asChild>
            <Option>
              <span>{selected.label}</span>
              <small>{selected.description}</small>
            </Option>
          </Select.Value>
          <Select.Icon asChild>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m5 7 5 5 5-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Select.Icon>
        </Trigger>
        <Select.Portal container={fieldRef.current?.closest<HTMLElement>('[role="dialog"]')}>
          <Menu
            position="popper"
            sideOffset={4}
            collisionPadding={12}
            onEscapeKeyDown={(event) => {
              event.preventDefault();
              setOpen(false);
            }}
          >
            <Select.Viewport>
              {options.map((option) => (
                <Item
                  key={option.value}
                  value={option.value}
                  textValue={option.label}
                >
                  <Select.ItemText asChild>
                    <Option>
                      <span>{option.label}</span>
                      <small>{option.description}</small>
                    </Option>
                  </Select.ItemText>
                </Item>
              ))}
            </Select.Viewport>
          </Menu>
        </Select.Portal>
      </Select.Root>
    </Field>
  );
}

export function SimpleStyleControlFields({ group, controls, onChange }: { group: "foundation" | "limits"; controls: SimpleStyleControls; onChange: (controls: SimpleStyleControls) => void }) {
  return (
    <Fields>
      {group === "limits" && <Notice>Some models have stricter content limits than others, so your selected settings may not always be fully reflected in the output.</Notice>}
      {SIMPLE_STYLE_CONTROLS.filter((control) => control.group === group).map((control) => (
        <ControlField
          key={control.id}
          control={control}
          value={controls[control.id]}
          onChange={(value) => onChange({ ...controls, [control.id]: value })}
        />
      ))}
    </Fields>
  );
}

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;
const Field = styled.div`
  min-width: 0;
  label {
    display: block;
    margin-bottom: 10px;
    font-size: 14px;
    font-weight: 600;
  }
`;
const Trigger = styled(Select.Trigger)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 56px;
  padding: 8px 10px;
  border: 1px solid var(--grey-3);
  border-radius: 10px;
  background: var(--menu-color);
  color: var(--grey-0);
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  svg {
    flex-shrink: 0;
    color: var(--grey-2);
  }
  &:focus-visible {
    outline: 2px solid var(--selected-element-text);
    outline-offset: 2px;
  }
`;
const Menu = styled(Select.Content)`
  z-index: 1000000001;
  width: var(--radix-select-trigger-width);
  max-height: min(420px, var(--radix-select-content-available-height));
  overflow-y: auto;
  border-radius: 12px;
  padding: 4px;
  background: var(--menu-color);
  color: var(--grey-0);
  box-shadow: var(--default-drop-shadow);
`;
const Item = styled(Select.Item)`
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  outline: none;
  &[data-state="checked"] {
    background: var(--selected-element-background);
  }
  &[data-highlighted] {
    background: var(--menu-color-hover);
  }
`;
const Option = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  line-height: 1.3;
  > span {
    font-weight: 500;
  }
  small {
    font-size: 13px;
    font-weight: 400;
    color: var(--grey-1);
  }
`;
const Notice = styled.p`
  margin: 0;
  color: var(--grey-1);
  font-size: 13px;
  line-height: 1.4;
`;
